import { store } from '@/store'
import { employeesApi } from '@/store/services'
import { toOptions } from '@/utils'
import { useEffect, useState } from 'react'
import { createSearchParams } from 'react-router-dom'
import { AsyncPaginate } from 'react-select-async-paginate'
import { SelectWrapper } from '../wrapper'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

export const EmployeeSelector = ({ value, onChange, includeHistory = false, light = false, perPage = 10 }) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState({})

  const loadOptions = async (search, _loadedOptions, { page }) => {
    if (light) {
      const { data, meta } = await store.dispatch(
        employeesApi.endpoints.getEmployeesSelect.initiate({
          q: search,
          page,
          perPage,
          includeHistory,
          order: { field: 'name', sort: 'asc' },
        }, { subscribe: true })
      ).unwrap()

      const options = data.map(e => ({
        value: e.id,
        // etiqueta corta para selector liviano
        label: `${e.name} (${e.document_number || '-'})`.trim()
      }))

      const hasMore = page < (meta?.pagination?.total_pages || 1)

      return {
        options,
        hasMore,
        additional: { page: page + 1 }
      }
    } else {
      const searchParams = createSearchParams({
        page,
        per_page: perPage,
        q: search,
        'order[field]': 'name',
        'order[sort]': 'asc',
        ...(includeHistory ? { include_history: 'true' } : {})
      }).toString()

      const { employees, meta } = await store.dispatch(
        employeesApi.endpoints.getEmployees.initiate(`?${searchParams}`, { subscribe: true })
      ).unwrap()

      // usa el fullName que venga del serializer del index completo
      const options = toOptions({ data: employees, label: 'fullName' })
      const hasMore = page < meta.pagination.total_pages

      return {
        options,
        hasMore,
        additional: { page: page + 1 }
      }
    }
  }

  const getCurrentEmployee = async (id) => {
    const employee = await store.dispatch(
      employeesApi.endpoints.getEmployee.initiate({ id })
    ).unwrap()

    // si vienes del endpoint liviano, construye etiqueta consistente
    const label = employee.fullName || `${employee.name} ${employee.last_name}`.trim()
    setSelectedOption({ value: employee.id, label })
  }

  useEffect(() => {
    if (!value) return
    if (value === selectedOption.value) return
    getCurrentEmployee(value)
  }, [value])

  const handleChange = (option = {}) => {
    setSelectedOption(option)
    onChange(option)
  }

  return (
    <SelectWrapper label={t('employee.one')}>
      {(styles) => (
        <AsyncPaginate
          loadingMessage={() => t('select.loading')}
          value={selectedOption?.value ? selectedOption : null}
          debounceTimeout={600}
          placeholder={t('select.one')}
          onChange={handleChange}
          loadOptions={loadOptions}
          styles={styles}
          menuPortalTarget={document.body}
          isClearable
          additional={{ page: 1 }}
        />
      )}
    </SelectWrapper>
  )
}

EmployeeSelector.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.number,
  includeHistory: PropTypes.bool,
  light: PropTypes.bool,
  perPage: PropTypes.number
}